import {
  KeyframeTrack,
  NumberKeyframeTrack,
  Object3D,
} from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

/**
 * When clip.userData.morphTracks is set (see BVHLoader), rewrite those driver
 * tracks into three.js morphTargetInfluences tracks using the loaded character scene.
 */
export function bvhHasMorphDriverTracks(bvh: BVH): boolean {
  const morphTracks = (
    bvh.clip as unknown as { userData?: { morphTracks?: KeyframeTrack[] } }
  ).userData?.morphTracks;
  return Boolean(morphTracks && morphTracks.length > 0);
}

/**
 * Clone skeleton + clip, copy clip userData, append morphTargetInfluences tracks.
 * Caller should pass a BVH already normalized for playback (morph joint position
 * tracks removed from clip.tracks; morphTracks still in userData).
 */
export function applyMorphTracksToBvh(
  bvh: BVH,
  characterScene: Object3D,
): BVH {
  const clip = bvh.clip.clone();
  const srcUserData = (
    bvh.clip as unknown as { userData?: Record<string, unknown> }
  ).userData;
  (clip as unknown as { userData?: Record<string, unknown> }).userData =
    srcUserData ? { ...srcUserData } : {};

  const out: BVH = {
    skeleton: bvh.skeleton.clone(),
    clip,
  };

  appendMorphInfluenceTracks(out, characterScene);
  return out;
}

function appendMorphInfluenceTracks(
  animationData: BVH,
  characterScene: Object3D,
) {
  const userData = (
    animationData.clip as unknown as { userData?: Record<string, unknown> }
  ).userData;
  const morphTracks = userData?.morphTracks as KeyframeTrack[] | undefined;
  if (!morphTracks || morphTracks.length === 0) return;

  const newTracks: KeyframeTrack[] = [];
  for (const sourceTrack of morphTracks) {
    if (sourceTrack.getValueSize() !== 3) continue;
    const morphName = sourceTrack.name.replace(/\.position$/, '');
    const sourceValues = sourceTrack.values;
    const xValues = new Float32Array(sourceValues.length / 3);
    for (let i = 0; i < xValues.length; i++) {
      xValues[i] = sourceValues[i * 3]!;
    }
    characterScene.traverse((child) => {
      const meshLike = child as Object3D & {
        morphTargetDictionary?: Record<string, number>;
      };
      if (
        !meshLike.morphTargetDictionary ||
        !(morphName in meshLike.morphTargetDictionary)
      ) {
        return;
      }
      const trackName = `${child.name}.morphTargetInfluences[${morphName}]`;
      newTracks.push(
        new NumberKeyframeTrack(
          trackName,
          Array.from(sourceTrack.times),
          Array.from(xValues),
        ),
      );
    });
  }

  animationData.clip.tracks.push(...newTracks);
}
